"use client";

import {
  useEffect,
  useCallback,
  useId,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Z } from "@/lib/ui/layers";
import { X } from "lucide-react";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Max width class. Default "max-w-md". */
  maxWidth?: string;
  children: ReactNode;
}

export function Dialog({
  open,
  onClose,
  title,
  maxWidth = "max-w-md",
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Escape key
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKey);
    // Trap focus — focus the panel on open
    const prev = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      prev?.focus();
    };
  }, [open, handleKey]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      /* Z.modal, not z-[200]: 200 lost to app chrome at 1000 and to a
         fullscreen frame at 10020, so a dialog opened from either was
         hidden behind it. One scale — src/lib/ui/layers.ts. */
      style={{ zIndex: Z.modal }}
      onClick={onClose}
    >
      {/* Backdrop — GAM Brand v2: blur strength from active skin */}
      <div
        className="absolute inset-0 bg-black/60"
        style={{
          backdropFilter: "var(--gam-blur-chrome, blur(8px))",
          WebkitBackdropFilter: "var(--gam-blur-chrome, blur(8px))",
        }}
      />

      {/* Panel — GAM Brand v2: hero radius + heavy shadow */}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          borderRadius: "var(--gam-radius-hero, 0.75rem)",
          boxShadow: "var(--gam-shadow-card-hover, 0 25px 50px -12px rgba(0,0,0,0.4))",
        }}
        className={[
          "gam-card relative z-10 w-full border border-white/[0.08] bg-slate-900 outline-none",
          "mx-4",
          maxWidth,
        ].join(" ")}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between border-b border-white/[0.06] px-5 py-3">
            <h2 id={titleId} className="text-sm font-semibold text-slate-200">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="סגור חלון"
              className="rounded-md p-1 text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-300"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>,
    document.body
  );
}
